import { computed, inject, Signal } from '@angular/core';
import { injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';

import {
  GqlService,
  listTrialSettingsQuery,
  TrialSettingKey,
  UpdateTrialSettingsInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { gqlToPromise } from '@shared/utils/gql-to-promise';

const trialSettingsKeys = {
  index: ['trial-settings'],
  trialSettings: (trialKey: string) =>
    [...trialSettingsKeys.index, 'trial-settings', { trialKey }] as const,
} as const;

export const injectTrialSettings = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const mainQuery = inject(MainQuery);

  return injectQuery(() => ({
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    queryFn: () => {
      return gqlToPromise(() => gqlService.listTrialSettings$());
    },
    queryKey: trialSettingsKeys.trialSettings(mainQuery.trialKey()),
  }));
};

export const injectTrialSettingByName = (
  settingKey: TrialSettingKey
): { isLoading: Signal<boolean>; setting: Signal<listTrialSettingsQuery | undefined> } => {
  const trialSettings = injectTrialSettings();

  return {
    isLoading: trialSettings.isLoading,
    setting: computed(() =>
      trialSettings.data()?.find((setting) => setting.setting_key === settingKey)
    ),
  };
};

export const injectUpdateTrialSettingMutation = () => {
  const gqlService = inject(GqlService);
  const overlayService = inject(OverlayService);
  const mainQuery = inject(MainQuery);
  const queryClient = inject(QueryClient);

  return injectMutation(() => ({
    mutationFn: (input: UpdateTrialSettingsInput) => {
      return gqlToPromise(() => gqlService.updateTrialSettings$(input));
    },
    onError: (error: Error) => {
      overlayService.error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: trialSettingsKeys.trialSettings(mainQuery.trialKey()),
      });
    },
  }));
};
